// src/utils/Onboarding/FirebaseHairAnalysis.js

import { getStorage, ref, uploadBytes, uploadBytesResumable } from 'firebase/storage';

/**
 * Upload image blob to Firebase Storage under a time-coded random path.
 * Returns { fileName, uploadPromise }.
 */
export function uploadImageToStorage({
  blob,
  folder = 'image_analysis_test',
  fileExt = 'jpg',
  onProgress, // optional: (fraction 0..1, snapshot) => void
}) {
  if (!blob) throw new Error('No image blob to upload');
  const storage = getStorage();
  const ts = Date.now();
  const rand = Math.random().toString(36).slice(2, 8);
  const fileName = `${folder}/${ts}_${rand}.${fileExt}`;

  const storageRef = ref(storage, fileName);
  // Prefer resumable to expose progress. Fall back to uploadBytes for environments without observers.
  let uploadPromise;
  try {
    const task = uploadBytesResumable(storageRef, blob);
    uploadPromise = new Promise((resolve, reject) => {
      task.on('state_changed', (snapshot) => {
        if (typeof onProgress === 'function') {
          const total = snapshot.totalBytes || 0;
          const transferred = snapshot.bytesTransferred || 0;
          const frac = total > 0 ? Math.min(1, Math.max(0, transferred / total)) : 0;
          try { onProgress(frac, snapshot); } catch {}
        }
      }, (error) => {
        console.error('[RemoteAnalysis] Upload failed:', error);
        reject(error);
      }, () => {
        resolve(fileName);
      });
    });
  } catch (e) {
    // Safe fallback
    uploadPromise = uploadBytes(storageRef, blob)
      .then((snap) => {
        if (typeof onProgress === 'function') {
          try { onProgress(1, snap); } catch {}
        }
        return fileName;
      })
      .catch((err) => {
        console.error('[RemoteAnalysis] Upload failed:', err);
        throw err;
      });
  }

  return { fileName, uploadPromise };
}

/**
 * Trigger analyzeStorageImage HTTPS function for a given storage fileName.
 * Returns a promise that resolves with the JSON result.
 * 
 * @param {Object} options
 * @param {string} options.fileName - Firebase Storage path of the input image
 * @param {string} [options.analyzeBaseUrl] - Optional override for API base URL
 * @param {Object} [options.reportMetadata] - Optional metadata for report viewing feature
 * @param {string} [options.reportMetadata.userName] - User's name for UI personalization
 * @param {number} [options.reportMetadata.userAge] - User's age for score calculations
 * @param {number} [options.reportMetadata.imageWidth] - Original image width in pixels
 * @param {number} [options.reportMetadata.imageHeight] - Original image height in pixels
 * @param {Object} [options.reportMetadata.hairGeometry] - MediaPipe computed hair geometry
 * @returns {Promise<Object>} API response with analysis results
 */
export async function triggerStorageHairAnalysis({
  fileName,
  analyzeBaseUrl,
  reportMetadata,
}) {
  if (!fileName) throw new Error('fileName is required to trigger analysis');

  // Safely read env overrides
  let viteUrl;
  try {
    viteUrl = typeof import.meta !== 'undefined' ? import.meta.env?.VITE_ANALYZE_STORAGE_URL : undefined;
  } catch (_) {
    viteUrl = undefined;
  }

  const reactUrl =
    typeof process !== 'undefined' && process && process.env
      ? process.env.REACT_APP_ANALYZE_STORAGE_URL
      : undefined;

  // IMPORTANT: default to actual deployed region (europe-west3)
  const baseUrl =
    analyzeBaseUrl ||
    viteUrl ||
    reactUrl ||
    'https://europe-west3-match-medic-p0.cloudfunctions.net/analyzeStorageImage';

  const urlPost = `${baseUrl.replace(/\/$/, '')}/analyze`;
  const urlGet = `${baseUrl.replace(/\/$/, '')}/analyze/${encodeURIComponent(fileName)}`;

  // Build request body with optional reportMetadata
  const requestBody = { fileName };
  if (reportMetadata && typeof reportMetadata === 'object') {
    requestBody.reportMetadata = reportMetadata;
    console.log('[RemoteAnalysis] Including reportMetadata:', Object.keys(reportMetadata));
  }

  // Try POST first
  try {
    const resp = await fetch(urlPost, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      mode: 'cors',
      body: JSON.stringify(requestBody),
    });

    const text = await resp.text();
    let json;
    try { json = JSON.parse(text); } catch { json = { raw: text }; }

    if (!resp.ok || json?.success === false) {
      console.error('[RemoteAnalysis] POST error:', { status: resp.status, body: json });
      throw new Error(json?.error || `Function HTTP ${resp.status}`);
    }

    return json;
  } catch (postErr) {
    const resp2 = await fetch(urlGet, {
      method: 'GET',
      headers: { Accept: 'application/json' },
      mode: 'cors',
    });

    const text2 = await resp2.text();
    let json2;
    try { json2 = JSON.parse(text2); } catch { json2 = { raw: text2 }; }

    if (!resp2.ok || json2?.success === false) {
      console.error('[RemoteAnalysis] GET error:', { status: resp2.status, body: json2 });
      throw new Error(json2?.error || `Function HTTP ${resp2.status}`);
    }

    return json2;
  }
}

/**
 * Backwards-compatible helper that both uploads and triggers the function automatically.
 * Prefer using uploadImageToStorage + triggerStorageHairAnalysis to control timing.
 */
export function startStorageHairAnalysis({
  blob,
  folder = 'image_analysis_test',
  fileExt = 'jpg',
  analyzeBaseUrl,
}) {
  const { fileName, uploadPromise } = uploadImageToStorage({ blob, folder, fileExt });
  const analyzePromise = uploadPromise.then(() => triggerStorageHairAnalysis({ fileName, analyzeBaseUrl }));
  return { fileName, uploadPromise, analyzePromise };
}
